import { useContext, useEffect, useRef, useState, Suspense, lazy } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Dialog, CircularProgress } from '@mui/material'

// Lazy load DayViewCalendarComponent - it's a large component (3,672 lines) with many dependencies
const DayViewCalendarComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/DayViewCalendarComponent')
)
import { PastTherapyReportDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import NewBookingPopup from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/NewBookingPopup'
import { fetchDrugAdviceEntity } from 'src/clinic/reducers/prescription/services/drugAdviceItems'
import {
  HE_DRUG_ADVICE_DOSAGE,
  HE_DRUG_ADVICE_DURATION,
  HE_DRUG_ADVICE_FREQUENCY,
  HE_DRUG_ADVICE_NOTE,
  HE_DRUG_ADVICE_TIMING,
  HE_DRUG_ADVICE_TYPE,
} from 'src/consumer/constants'
import useElementDimensions, {
  useIsMentorCheckedInOrPeekedInValidity,
} from 'src/customHooks/customHooks'
import HeaderItems from 'src/mentor/components/MentorClinicBookingsPage/component/HeaderItems'
import PatientSearchDrawer from 'src/mentor/components/MentorClinicBookingsPage/component/PatientSearchDrawer'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorClinicsContext } from 'src/mentor/layout'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { getDoctorRecommendedHealthEntities } from 'src/reducers/doctorRecommendedHealthEntities/services/doctorRecommendedHealthEntities'
import { getScreeningReportTemplateData } from 'src/reducers/screeningReportTemplate/services/screeningReportTemplate'
import {
  AvailabilityPageContainer,
  PageHeadlineComponent,
  PageMainContentContainer,
} from '../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { MENTOR_CLINIC_BOOKING_TABS } from './util'

export default function MentorClinicBookingsPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { clinicId, clinicBrandId } = useParams()
  const { mentorProfileData, mentorClinicAssociationData, mentorChamberRoomsData } =
    useContext(MentorProfileContext)
  const [selectedTab, setSelectedTab] = useState(MENTOR_CLINIC_BOOKING_TABS.calendar.id)
  const [isPatientSearchOpen, setIsPatientSearchOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const dayViewCalendarRef = useRef(null)

  const clinicsContext = useContext(MentorClinicsContext) || {}
  const { clinicEventTypesMap } = clinicsContext
  const currentClinicEvents = clinicEventTypesMap?.[clinicId]

  const { ref: headerRef, dimensions } = useElementDimensions()
  const headerHeight = dimensions?.height || 80

  const { isCheckedIn, isPeekedIn } = useIsMentorCheckedInOrPeekedInValidity({
    byPassMentorIdCheck: true,
  })

  // Load non-critical prescription data after initial render
  // These are only needed when creating/editing prescriptions, not for calendar view
  useEffect(() => {
    console.log({ mentorClinicAssociationData, clinicId })

    if (
      mentorClinicAssociationData &&
      mentorClinicAssociationData?.length > 0 &&
      mentorClinicAssociationData.some((clinic) => clinic?.id === clinicId)
    ) {
      if (getAllRolesInBoolean().isDoctor) {
        // Defer non-critical data fetching to improve initial page load
        // Use requestIdleCallback when available, otherwise setTimeout
        const loadNonCriticalData = () => {
          // Screening report template - only needed when creating screening reports
          dispatch(getScreeningReportTemplateData({ clinicBrandId }))
          
          // Doctor recommended health entities - only needed when creating prescriptions
          dispatch(getDoctorRecommendedHealthEntities({ clinicBrandId }))
          
          // Drug advice entities - only needed when creating prescriptions
          // These can be loaded in parallel
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_TYPE }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_TIMING }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_FREQUENCY }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_DURATION }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_NOTE }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_DOSAGE }))
        }

        // Use requestIdleCallback if available (loads when browser is idle)
        // Falls back to setTimeout for older browsers
        if ('requestIdleCallback' in window) {
          requestIdleCallback(loadNonCriticalData, { timeout: 2000 })
        } else {
          // Fallback: delay by 500ms to allow initial render to complete
          setTimeout(loadNonCriticalData, 500)
        }
      }
    } else {
      console.log('TRIED TO NAVIGATE TO MENTOR HOME')
      // navigate('/mentor-home')
    }
  }, [])

  useEffect(() => {
    if (!isCheckedIn && !isPeekedIn) {
      navigate('/mentor-home/my-clinic-associations')
    }
  })

  const thisClinicData = mentorClinicAssociationData?.find((item) => item?.id === clinicId)

  async function refreshData() {
    if (!dayViewCalendarRef.current) {
      return
    }
    await dayViewCalendarRef.current?.getAndSetBookedSlotsOfSelectedDate()
  }

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient)
    // Patient is selected, close drawer and proceed with booking
  }

  // Prevent scrolling above the fixed header and ensure page starts at top
  useEffect(() => {
    // Track last scroll position to detect programmatic scrolling
    let lastScrollY = window.scrollY
    let scrollChangeTimeout = null
    let isAutoScrolling = false
    
    // Ensure page starts at top immediately
    const resetScroll = () => {
      // Don't reset if auto-scroll is in progress
      if (isAutoScrolling) {
        return
      }
      if (window.scrollY < 0 || document.documentElement.scrollTop < 0 || document.body.scrollTop < 0) {
        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }
    }
    
    // Reset on mount immediately (before auto-scroll)
    resetScroll()
    
    // Delay subsequent resets to allow auto-scroll to complete
    // Auto-scroll happens at ~1000ms, so delay our resets until after that
    const timeoutId = setTimeout(() => {
      resetScroll()
    }, 1500) // After auto-scroll completes
    const timeoutId2 = setTimeout(() => {
      resetScroll()
    }, 2000) // Additional safety check
    
    const isInsideDropdownMenu = (element) => {
      if (!element || typeof element.closest !== 'function') {
        return false
      }
      // Check for Material-UI Menu components
      const muiMenu = element.closest('[class*="MuiMenu-root"]') ||
                      element.closest('[class*="MuiMenu-paper"]') ||
                      element.closest('[class*="MuiMenu-list"]') ||
                      element.closest('[class*="MuiMenuList-root"]') ||
                      element.closest('[class*="MuiMenuItem-root"]') ||
                      element.closest('[class*="MuiPaper-root"][role="presentation"]')
      // Check for react-select menus
      const reactSelectMenu = element.closest('[class*="menu"]') || 
                              element.closest('[id*="react-select"]') ||
                              element.closest('[class*="MenuList"]') ||
                              element.closest('[class*="menu-list"]') ||
                              element.closest('[class*="react-select"]')
      return muiMenu || reactSelectMenu
    }
    
    const isDrawerOpen = () => {
      // Check if the search drawer is open by looking for the MUI drawer element
      // Check for drawer with aria-hidden="false" or drawer paper element
      const drawer = document.querySelector('[class*="MuiDrawer-root"][aria-hidden="false"], [class*="MuiDrawer-paper"][aria-hidden="false"]')
      return drawer !== null
    }
    
    const handleScroll = (e) => {
      // If drawer is open, don't interfere with scroll events
      if (isDrawerOpen()) {
        return
      }
      
      // Check if there's an active dropdown menu open (Material-UI Menu or react-select)
      const activeDropdown = document.querySelector(
        '[class*="MuiMenu-root"][aria-hidden="false"], ' +
        '[class*="MuiMenu-paper"][aria-hidden="false"], ' +
        '[class*="menu"][class*="is-open"], ' +
        '[id*="react-select"][aria-expanded="true"]'
      )
      
      // If dropdown is open, allow all scrolling
      if (activeDropdown) {
        return
      }
      
      // Detect rapid scroll changes (indicating programmatic scroll like scrollIntoView)
      const currentScrollY = window.scrollY
      const scrollDelta = Math.abs(currentScrollY - lastScrollY)
      lastScrollY = currentScrollY
      
      // If scroll changed significantly (>50px), it might be programmatic scrolling
      if (scrollDelta > 50) {
        isAutoScrolling = true
        // Clear existing timeout
        if (scrollChangeTimeout) {
          clearTimeout(scrollChangeTimeout)
        }
        // Reset flag after scroll completes (smooth scrolls take ~500-1000ms)
        scrollChangeTimeout = setTimeout(() => {
          isAutoScrolling = false
        }, 1500)
      }
      
      // Only reset negative scrolling (scrolling above 0) - use requestAnimationFrame to avoid interfering
      // with normal scroll events that the floating header needs
      const scrollY = window.scrollY
      const docScrollTop = document.documentElement.scrollTop
      const bodyScrollTop = document.body.scrollTop
      
      // Only reset if scroll is actually negative (not just 0) and not auto-scrolling
      if (!isAutoScrolling && (scrollY < 0 || docScrollTop < 0 || bodyScrollTop < 0)) {
        // Use requestAnimationFrame to reset on next frame, allowing scroll events to propagate first
        requestAnimationFrame(() => {
          resetScroll()
        })
      }
    }
    
    const handleWheel = (e) => {
      // If drawer is open, don't interfere with wheel events
      if (isDrawerOpen()) {
        return
      }
      
      // Check if the wheel event is inside a dropdown menu (Material-UI Menu or react-select)
      const target = e.target
      if (isInsideDropdownMenu(target)) {
        // Allow scrolling inside dropdowns - don't prevent default
        return
      }
      
      // Check if there's an active Material-UI Menu open
      const activeMenu = document.querySelector('[class*="MuiMenu-root"][aria-hidden="false"]')
      if (activeMenu && activeMenu.contains(target)) {
        // Allow scrolling inside Material-UI Menu
        return
      }
      
      // Only prevent scrolling up when actually at the very top of the page
      // Check all scroll positions to be accurate
      const scrollY = window.scrollY
      const docScrollTop = document.documentElement.scrollTop
      const bodyScrollTop = document.body.scrollTop
      const scrollTop = scrollY || docScrollTop || bodyScrollTop
      
      // Only prevent if we're at or above the top (<= 0) AND trying to scroll up
      // Allow all downward scrolling (deltaY > 0) and all upward scrolling when scrollY > 0
      const isAtTop = scrollTop <= 0
      const isScrollingUp = e.deltaY < 0
      
      // Very strict: only prevent when exactly at top (0 or negative) AND scrolling up
      // This allows normal upward scrolling when not at the top
      if (isAtTop && isScrollingUp) {
        // Only prevent if we're truly at the top - allow any scrolling when scrollY > 0
        e.preventDefault()
        e.stopPropagation()
        return false
      }
      
      // Allow all other scrolling to proceed normally
    }
    
    // Use passive listeners for scroll (better performance) - allows floating header to detect scroll
    // Passive listeners don't block scrolling and allow the floating header to work properly
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('scroll', handleScroll, { passive: true })
    // Use bubble phase (not capture) to allow normal scroll behavior first
    // Only prevent when we're actually at the top and scrolling up
    window.addEventListener('wheel', handleWheel, { passive: false })
    document.addEventListener('wheel', handleWheel, { passive: false })
    
    // Removed requestAnimationFrame loop - it was interfering with normal scrolling
    // The scroll event handler is sufficient to catch and reset negative scrolling
    
    return () => {
      clearTimeout(timeoutId)
      clearTimeout(timeoutId2)
      if (scrollChangeTimeout) {
        clearTimeout(scrollChangeTimeout)
      }
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('scroll', handleScroll)
      window.removeEventListener('wheel', handleWheel)
      document.removeEventListener('wheel', handleWheel)
    }
  }, [])

  if (!isCheckedIn && !isPeekedIn) {
    navigate('/mentor-home/my-clinic-associations')
  }

  return (
    <AvailabilityPageContainer 
      style={{ 
        marginTop: 0, 
        paddingTop: 0, 
        margin: 0, 
        padding: 0,
        position: 'relative',
        top: 0,
        left: 0,
        overflowY: 'visible',
        overflowX: 'hidden',
        minHeight: '100vh',
      }}
    >
      <PageHeadlineComponent
        fullWidthHeader
        noPadding
        pageContainerCustomStyles={{
          padding: 0,
          margin: 0,
          marginTop: 0,
          marginBottom: '0',
          position: 'fixed',
          top: '0px',
          left: '0px',
          right: '0px',
          width: '100%',
          zIndex: '15',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
        pageHeadlineCustomStyles={{ padding: 0, margin: 0 }}
        pageContainerRef={headerRef}
        pageContainerDataAttributes={{ 'data-portal-header': 'true' }}
        heading={
          <HeaderItems
            clinicBrandId={clinicBrandId}
            clinicId={clinicId}
            clinicAssociationData={mentorClinicAssociationData}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            mentorProfileData={mentorProfileData}
            refreshData={refreshData}
            hideRefreshButton
            onOpenPatientSearch={() => setIsPatientSearchOpen(true)}
          />
        }
      />

      <PageMainContentContainer
        noMaxWidth
        style={{
          margin: '0',
          marginTop: '0',
          paddingTop: '0',
          padding: '0',
          height: selectedTab === MENTOR_CLINIC_BOOKING_TABS.roster.id ? '100%' : 'auto',
          minHeight: '0',
        }}
      >
        <Box 
          sx={{ 
            height: `${headerHeight}px`, 
            flexShrink: 0,
            margin: 0,
            padding: 0,
            minHeight: `${headerHeight}px`,
            maxHeight: `${headerHeight}px`,
          }} 
        />
        {mentorProfileData && mentorClinicAssociationData && thisClinicData && (
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  width: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <DayViewCalendarComponent
              key={clinicId}
              forMentor={true}
              currentView={selectedTab}
              bookingLisitingApiSignature={`/api/secure/teacher/booked-slots/all-with-patient-info?get-patient-details=true`}
              publicBookingJoiningLinksApiSignature={`/api/secure/teacher/booked-slots/video-session-tokens/get`}
              sendPublicSessionLinkToPatientSignature="/api/secure/teacher/booked-slots/public-join-link/send"
              sendSecureSessionLinkToPatientSignature="/api/secure/teacher/booked-slots/secure-join-link/send"
                          pastPatientBookingsApiSignature="/api/secure/teacher/booked-event-history-of-a-patient"
              patientUpdateApiSignature="/api/secure/patient/update"
              patientTagsApiSignature="/api/secure/teacher/patient/tags"
              mentorAuthorisedToSaveAndGetPatientsUsingIdb={getAllRolesInBoolean().isDoctor}
              mentorId={mentorProfileData?.id}
              ContainerStyledComponent={PageMainContentContainer}
              mentorProfileData={mentorProfileData}
              thisClinicData={thisClinicData}
              headerContainerStyles={{ position: 'relative', top: '0px', marginTop: 0, paddingTop: 0, zIndex: 10 }}
              mentorClinicAssociationData={mentorClinicAssociationData}
              chambersData={mentorChamberRoomsData}
              meetingBookApiSignature="/api/secure/meeting/book"
              compactUI
              ref={dayViewCalendarRef}
              showIdlePopup
              showValidateCheckInPopup
              emptySlotClickFunctionOverride={
                mentorProfileData?.teacherUnEditableFields?.canOnlyViewTheirOwnPatients
                  ? () => {}
                  : null
              }
              hideToggleSwitchForReportRelatedData={
                !mentorProfileData?.showReportsRelatedDataInBookingCalendar
              }
              onOpenPatientSearch={() => setIsPatientSearchOpen(true)}
              // LeftSideExtraComponent={<CheckInInformationComponent />}
              // RightSideExtraComponent={
              //   <CheckoutButtonComponent
              //     text="Checkout"
              //     styleProp={{
              //       width: '100px',
              //       flexDirection: 'column',
              //       fontSize: '12px',
              //       borderRadius: '0px',
              //       padding: 0,
              //       height: '100%',
              //     }}
              //     showExitButtonIfNotCheckedIn={true}
              //   />
              // }
              useClinicAppointmentTypes={false}
              clinicAppointmentTypesList={currentClinicEvents}
              vitalsFormGetApiSignature="/api/secure/practitioner/vitals/form"
            />
          </Suspense>
        )}
      </PageMainContentContainer>

      {/* Patient Search Drawer */}
      <PatientSearchDrawer
        open={isPatientSearchOpen}
        onClose={() => setIsPatientSearchOpen(false)}
        onSelectPatient={handlePatientSelect}
        onBookAppointment={(patient) => {
          if (dayViewCalendarRef.current?.openBookingWithPatient) {
            dayViewCalendarRef.current.openBookingWithPatient(patient)
            setIsPatientSearchOpen(false)
          }
        }}
        clinicId={clinicId}
        clinicBrandId={clinicBrandId}
        mentorProfileData={mentorProfileData}
        searchPatientApiSignature="/api/secure/mentor/patient/list"
        createPatientApiSignature="/api/secure/patient/new"
        patientTagsApiSignature="/api/secure/teacher/patient/tags"
        canViewPatientHistory={true}
        patientHistoryGetApiSignature="/api/secure/patient/reports/list"
      />

      {/* Selected Patient Booking Dialog */}
      {selectedPatient && (
        <Dialog
          open={!!selectedPatient}
          onClose={() => setSelectedPatient(null)}
          PaperComponent={PastTherapyReportDialogPaper}
          sx={{
            '@media (max-width: 768px)': {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
                padding: 0,
              },
              '& .MuiDialog-paper': {
                margin: 0,
                maxHeight: '100vh',
                height: '100vh',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
              },
              '& .MuiDialogContent-root': {
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
              },
            },
          }}
        >
          <NewBookingPopup
            selectedPatient={selectedPatient}
            refreshData={refreshData}
            mentorsList={[]}
            appointmentTypesList={currentClinicEvents || []}
            closeDialog={() => setSelectedPatient(null)}
            patientHistoryGetApiSignature="/api/secure/teacher/booked-event-history-of-a-patient"
            hideSendToDoctorButton={false}
          />
        </Dialog>
      )}
    </AvailabilityPageContainer>
  )
}
